import type { AppId } from "@/lib/apps";

export type TierId =
  | "tier_signal"
  | "tier_pulse"
  | "tier_surge"
  | "tier_spectrum"
  | "tier_attuned";

export type KycStatus =
  | "unverified"
  | "pending"
  | "verified"
  | "failed"
  | "rejected"
  | "requires_input"
  | "canceled";

export type KycProvider = "stripe_identity" | "veriff" | "onfido" | "persona";

export type FriendshipLane = "dating" | "friendship" | "both" | "mentorship";

export interface ProfilePrompt {
  key: string;
  text: string;
  answer: string;
}

export interface PromptBankEntry {
  id: string;
  app: AppId;
  prompt_key: string;
  prompt_text: string;
  sort_order: number;
}

export const MAX_PHOTOS = 12;
export const MAX_PROMPTS = 8;
export const MIN_PROMPTS = 3;

export interface Profile {
  id: string;
  username: string | null;
  display_name: string | null;
  legal_first_name: string | null;
  legal_last_name: string | null;
  avatar_url: string | null;
  bio: string | null;

  dob: string | null;
  pronouns: string | null;
  location_text: string | null;
  location_lat: number | null;
  location_lng: number | null;

  primary_app: AppId | null;
  active_app: AppId | null;
  tier: TierId;
  trial_ends_at: string | null;

  gender_identity: string | null;
  sexual_orientation: string | null;
  neurodivergence: string[] | null;

  kyc_status: KycStatus;
  kyc_provider: string | null;
  kyc_verified_at: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  ai_training_opt_in: boolean;

  incognito: boolean;
  amplify_until: string | null;
  priority_match_until: string | null;

  // Phase 6 — Profile v2
  profile_id: string | null;
  photos: string[];
  prompts: ProfilePrompt[];
  voice_intro_url: string | null;
  voice_intro_text: string | null;
  video_intro_url: string | null;
  video_intro_poster: string | null;
  friendship_lane: FriendshipLane;
  is_roaming: boolean;
  roam_until: string | null;
  is_amplified: boolean;
  amplified_until: string | null;
  is_priority: boolean;
  priority_until: string | null;
  bgc_verified: boolean;
  community_verified: boolean;
  resonate_enabled: boolean;
  replay_credits: number;
  acknowledged_emergency_at: string | null;

  onboarding_completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export const TIER_LABELS: Record<TierId, string> = {
  tier_signal: "Signal",
  tier_pulse: "Pulse",
  tier_surge: "Surge",
  tier_spectrum: "Spectrum",
  tier_attuned: "Attuned",
};
